// ============================================================
//  src/pages/bots/tabs/StorageTab.js
//
//  Browse, edit, and clear the bot_storage_kv store.
//  Two-panel layout:
//    Left  — list of chatIds that have storage (paginated)
//    Right — key/value rows for the selected chat,
//            with inline edit and per-key delete.
//
//  API calls (added to client.js separately):
//    listStorageChats(bId, limit, offset)  GET  ?bId
//    listStorageEntries(bId, chatId)       GET  ?bId&chatId
//    clearStorageChat(bId, chatId)         POST action=clear_chat
//    clearStorageBot(bId)                  POST action=clear_bot
//    setStorageKey(bId, chatId, key, value) POST action=set
//    deleteStorageKey(bId, chatId, key)    POST action=delete_key
// ============================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  listStorageChats,
  listStorageEntries,
  clearStorageChat,
  clearStorageBot,
  setStorageKey,
  deleteStorageKey,
} from '../../../api/client';
import './tabs.css';
import './StorageTab.css';

// ── Helpers ───────────────────────────────────────────────────
function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const d    = new Date(dateStr.replace(' ', 'T'));
  const secs = Math.floor((Date.now() - d) / 1000);
  if (secs < 60)    return 'just now';
  if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function tryPrettyJson(str) {
  try {
    const parsed = JSON.parse(str);
    if (typeof parsed === 'object') return JSON.stringify(parsed, null, 2);
  } catch (_) {}
  return null;
}

const PAGE_SIZE = 50;

// ── Confirm modal ─────────────────────────────────────────────
function ConfirmModal({ title, message, confirmLabel = 'Delete', onConfirm, onClose }) {
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <div className="modal-title">⚠️ {title}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ color: 'var(--text-muted)', fontSize: 13.5, margin: 0 }}>{message}</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ── Edit modal ────────────────────────────────────────────────
function EditModal({ entry, bId, chatId, onSave, onClose }) {
  const [value, setValue] = useState(entry.skValue ?? '');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await setStorageKey(bId, chatId, entry.skKey, value);
      toast.success('Value updated');
      onSave(entry.skKey, value);
      onClose();
    } catch {
      toast.error('Failed to update value');
    }
    setSaving(false);
  }

  const pretty = tryPrettyJson(value);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <div className="modal-title">Edit Value</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 10 }}>
            <span className="st-key-chip">{entry.skKey}</span>
          </div>
          <textarea
            className="st-edit-textarea"
            value={value}
            onChange={e => setValue(e.target.value)}
            rows={pretty ? 8 : 4}
            placeholder="Enter value…"
            autoFocus
          />
          {pretty && (
            <div className="st-json-hint">
              <span>Detected JSON — preview:</span>
              <pre className="st-json-pre">{pretty}</pre>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add key modal ─────────────────────────────────────────────
function AddKeyModal({ bId, chatId, onSave, onClose }) {
  const [key,   setKey]   = useState('');
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleAdd() {
    const k = key.trim();
    if (!k) return toast.error('Key is required');
    setSaving(true);
    try {
      await setStorageKey(bId, chatId, k, value);
      toast.success('Key added');
      onSave(k, value);
      onClose();
    } catch {
      toast.error('Failed to add key');
    }
    setSaving(false);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div className="modal-title">Add Storage Key</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label className="form-label">Key</label>
            <input
              className="form-control"
              value={key}
              onChange={e => setKey(e.target.value)}
              placeholder="e.g. step, session_token, cart_total"
              autoFocus
            />
          </div>
          <div>
            <label className="form-label">Value</label>
            <textarea
              className="st-edit-textarea"
              value={value}
              onChange={e => setValue(e.target.value)}
              rows={3}
              placeholder="Value (string, number, or JSON)"
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>
            {saving ? 'Adding…' : 'Add Key'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Entry row ─────────────────────────────────────────────────
function EntryRow({ entry, bId, chatId, onUpdated, onDeleted }) {
  const [confirm, setConfirm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied]   = useState(false);

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteStorageKey(bId, chatId, entry.skKey);
      toast.success(`Deleted "${entry.skKey}"`);
      onDeleted(entry.skKey);
    } catch {
      toast.error('Delete failed');
    }
    setDeleting(false);
    setConfirm(false);
  }

  function copyValue() {
    navigator.clipboard.writeText(entry.skValue ?? '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  }

  const pretty   = tryPrettyJson(entry.skValue);
  const isJson   = !!pretty;
  const dispVal  = isJson
    ? JSON.stringify(JSON.parse(entry.skValue))   // compact single line
    : (entry.skValue ?? '');

  return (
    <>
      <div className="st-entry-row">
        <div className="st-entry-left">
          <span className="st-key-chip">{entry.skKey}</span>
          {isJson && <span className="st-json-badge">JSON</span>}
        </div>
        <div className="st-entry-val" title={entry.skValue ?? ''}>
          {dispVal || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>empty</span>}
        </div>
        <div className="st-entry-meta">{timeAgo(entry.skUpdatedAt)}</div>
        <div className="st-entry-actions">
          <button className="st-act-btn" title="Copy value" onClick={copyValue}>
            {copied ? '✓' : '⎘'}
          </button>
          <button className="st-act-btn" title="Edit" onClick={() => setEditing(true)}>✏️</button>
          <button
            className="st-act-btn st-act-danger"
            title="Delete key"
            onClick={() => setConfirm(true)}
            disabled={deleting}
          >
            🗑
          </button>
        </div>
      </div>

      {editing && (
        <EditModal
          entry={entry}
          bId={bId}
          chatId={chatId}
          onSave={(k, v) => onUpdated(k, v)}
          onClose={() => setEditing(false)}
        />
      )}
      {confirm && (
        <ConfirmModal
          title="Delete Key"
          message={`Delete "${entry.skKey}" from this user's storage? This cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onClose={() => setConfirm(false)}
        />
      )}
    </>
  );
}

// ── Right panel: entries for selected chat ────────────────────
function EntriesPanel({ bId, chatId, onBack }) {
  const [entries, setEntries]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search,  setSearch]        = useState('');
  const [confirm, setConfirm]       = useState(false);
  const [addOpen, setAddOpen]       = useState(false);
  const [clearing, setClearing]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listStorageEntries(bId, chatId);
      if (res.data?.success) setEntries(res.data.storage ?? []);
    } catch {
      toast.error('Failed to load storage entries');
    }
    setLoading(false);
  }, [bId, chatId]);

  useEffect(() => { load(); }, [load]);

  async function handleClearChat() {
    setClearing(true);
    try {
      await clearStorageChat(bId, chatId);
      toast.success('Storage cleared for this user');
      setEntries([]);
    } catch {
      toast.error('Clear failed');
    }
    setClearing(false);
    setConfirm(false);
  }

  function handleUpdated(key, value) {
    setEntries(prev => prev.map(e => e.skKey === key ? { ...e, skValue: value, skUpdatedAt: new Date().toISOString() } : e));
  }

  function handleDeleted(key) {
    setEntries(prev => prev.filter(e => e.skKey !== key));
  }

  function handleAdded(key, value) {
    setEntries(prev => {
      const existing = prev.find(e => e.skKey === key);
      if (existing) return prev.map(e => e.skKey === key ? { ...e, skValue: value, skUpdatedAt: new Date().toISOString() } : e);
      return [...prev, { skKey: key, skValue: value, skUpdatedAt: new Date().toISOString() }].sort((a, b) => a.skKey.localeCompare(b.skKey));
    });
  }

  const filtered = entries.filter(e =>
    !search || e.skKey.toLowerCase().includes(search.toLowerCase()) ||
    (e.skValue ?? '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="st-entries-panel">
      {/* Header */}
      <div className="st-entries-hdr">
        <button className="st-back-btn" onClick={onBack}>← Chats</button>
        <div className="st-entries-title">
          <span className="st-chat-id-label">{chatId}</span>
          <span className="st-entry-count">{entries.length} key{entries.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="st-entries-actions">
          <button className="btn btn-sm btn-outline" onClick={() => setAddOpen(true)}>+ Add Key</button>
          <button
            className="btn btn-sm btn-danger-outline"
            onClick={() => setConfirm(true)}
            disabled={clearing || entries.length === 0}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Search */}
      {entries.length > 4 && (
        <div className="st-search-wrap">
          <input
            className="form-control"
            placeholder="Search keys or values…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      )}

      {/* Entries */}
      {loading ? (
        <div className="tab-loading"><span className="spinner spinner-dark" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="tab-empty">
          <div className="tab-empty-icon">📦</div>
          <div className="tab-empty-title">{search ? 'No matching keys' : 'No storage entries'}</div>
          <div className="tab-empty-text">
            {search ? 'Try a different search term.' : 'This user has no stored variables yet.'}
          </div>
          {!search && (
            <button className="btn btn-outline" style={{ marginTop: 12 }} onClick={() => setAddOpen(true)}>
              + Add First Key
            </button>
          )}
        </div>
      ) : (
        <div className="st-entries-list">
          <div className="st-entries-table-hdr">
            <span>Key</span>
            <span>Value</span>
            <span>Updated</span>
            <span></span>
          </div>
          {filtered.map(entry => (
            <EntryRow
              key={entry.skKey}
              entry={entry}
              bId={bId}
              chatId={chatId}
              onUpdated={handleUpdated}
              onDeleted={handleDeleted}
            />
          ))}
        </div>
      )}

      {confirm && (
        <ConfirmModal
          title="Clear User Storage"
          message={`Delete all ${entries.length} key(s) for ${chatId}? This will reset their conversation state and cannot be undone.`}
          confirmLabel="Clear All"
          onConfirm={handleClearChat}
          onClose={() => setConfirm(false)}
        />
      )}
      {addOpen && (
        <AddKeyModal
          bId={bId}
          chatId={chatId}
          onSave={handleAdded}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}

// ── Main StorageTab component ─────────────────────────────────
export default function StorageTab({ bId }) {
  const [chats,      setChats]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(0);
  const [hasMore,    setHasMore]    = useState(false);
  const [selected,   setSelected]   = useState(null); // chatId
  const [confirmAll, setConfirmAll] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);

  const load = useCallback(async (pg = 0) => {
    setLoading(true);
    try {
      const offset = pg * PAGE_SIZE;
      const res    = await listStorageChats(bId, PAGE_SIZE + 1, offset);
      if (res.data?.success) {
        const raw = res.data.chats ?? [];
        setHasMore(raw.length > PAGE_SIZE);
        setChats(raw.slice(0, PAGE_SIZE));
        setPage(pg);
      }
    } catch {
      toast.error('Failed to load storage chats');
    }
    setLoading(false);
  }, [bId]);

  useEffect(() => { load(0); }, [load]);

  async function handleClearAll() {
    setClearingAll(true);
    try {
      await clearStorageBot(bId);
      toast.success('All bot storage cleared');
      setChats([]);
      setSelected(null);
    } catch {
      toast.error('Failed to clear storage');
    }
    setClearingAll(false);
    setConfirmAll(false);
  }

  const filtered = chats.filter(c =>
    !search || c.skChatId.toLowerCase().includes(search.toLowerCase())
  );

  // ── Entries panel view ──────────────────────────────────────
  if (selected) {
    return (
      <div className="tab-content">
        <EntriesPanel
          bId={bId}
          chatId={selected}
          onBack={() => setSelected(null)}
        />
      </div>
    );
  }

  // ── Chat list view ──────────────────────────────────────────
  return (
    <div className="tab-content">
      {/* Toolbar */}
      <div className="tab-toolbar">
        <div>
          <div className="tab-title">Storage Browser</div>
          <div className="tab-sub">Per-user key-value store set by your bot's actions</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-outline btn-sm" onClick={() => load(0)}>↺ Refresh</button>
          <button
            className="btn btn-sm btn-danger-outline"
            onClick={() => setConfirmAll(true)}
            disabled={clearingAll || chats.length === 0}
          >
            Clear All Storage
          </button>
        </div>
      </div>

      {/* Summary strip */}
      {chats.length > 0 && (
        <div className="st-summary-strip">
          <span>🗄 <strong>{chats.length}{hasMore ? '+' : ''}</strong> user{chats.length !== 1 ? 's' : ''} with storage</span>
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
            Click a user to inspect their variables
          </span>
        </div>
      )}

      {/* Search */}
      {chats.length > 4 && (
        <div className="st-search-wrap">
          <input
            className="form-control"
            placeholder="Search by chat ID / phone number…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="tab-loading"><span className="spinner spinner-dark" /> Loading storage…</div>
      ) : filtered.length === 0 ? (
        <div className="tab-empty">
          <div className="tab-empty-icon">📭</div>
          <div className="tab-empty-title">
            {search ? 'No matching users' : 'No storage data yet'}
          </div>
          <div className="tab-empty-text">
            {search
              ? 'Try a different phone number or chat ID.'
              : 'When your bot uses set_storage actions, user data will appear here.'}
          </div>
        </div>
      ) : (
        <div className="st-chat-list">
          {filtered.map(chat => (
            <button
              key={chat.skChatId}
              className="st-chat-row"
              onClick={() => setSelected(chat.skChatId)}
            >
              <div className="st-chat-avatar">
                {chat.skChatId.slice(-2)}
              </div>
              <div className="st-chat-info">
                <div className="st-chat-id">{chat.skChatId}</div>
                <div className="st-chat-meta">
                  <span>{chat.keyCount} key{chat.keyCount !== 1 ? 's' : ''}</span>
                  <span className="st-meta-sep">·</span>
                  <span>updated {timeAgo(chat.lastUpdated)}</span>
                </div>
              </div>
              <div className="st-chat-arrow">›</div>
            </button>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && (page > 0 || hasMore) && (
        <div className="st-pager">
          <button className="btn btn-outline btn-sm" disabled={page === 0} onClick={() => load(page - 1)}>
            ← Prev
          </button>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Page {page + 1}</span>
          <button className="btn btn-outline btn-sm" disabled={!hasMore} onClick={() => load(page + 1)}>
            Next →
          </button>
        </div>
      )}

      {confirmAll && (
        <ConfirmModal
          title="Clear All Bot Storage"
          message={`This will permanently delete ALL storage entries for every user of this bot. Their conversation state will be completely reset. This cannot be undone.`}
          confirmLabel="Clear Everything"
          onConfirm={handleClearAll}
          onClose={() => setConfirmAll(false)}
        />
      )}
    </div>
  );
}